import React, { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { BAND_CONTENT, DIMENSION_LABEL, DIMENSION_ORDER, dimensionLevel } from './scoring';
import { decodeScoreResult } from './scoreResultUrl';
import { AuditRequestForm } from './AuditRequestForm';
import { trackScoreComplete } from '../lib/analytics';
type ScoreResultsPageProps = {
  search: string;
};
export function ScoreResultsPage({
  search
}: ScoreResultsPageProps) {
  const result = decodeScoreResult(search);
  const totalScore = result?.totalScore;
  const band = result?.band;
  useEffect(() => {
    if (totalScore === undefined || !band) return;
    trackScoreComplete(totalScore, band);
  }, [totalScore, band]);
  if (!result) {
    return <EmptyScoreState data-id="element-515" data-mp="3jpliqsmp844c" />;
  }
  const content = BAND_CONTENT[result.band];
  return <main className="min-h-screen w-full bg-paper px-6 py-8 text-text md:px-10 md:py-12" data-id="element-516" data-mp="baa4br1po134">
      <div className="mx-auto max-w-4xl" data-id="element-517" data-mp="g1a4d564mz9u">
        <a href="/" className="inline-flex items-center gap-2 rounded-xl px-2 py-2 text-sm font-semibold text-navy transition-colors hover:bg-paper-dark focus:outline-none focus:ring-2 focus:ring-seaglass" data-id="element-518" data-mp="g33amjctvhhb">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-519" data-mp="rxygd19q5int" />
          Back to Author Leverage
        </a>

        <header className="border-b border-gray-200 pb-10 pt-10 text-center md:pb-12" data-id="element-520" data-mp="15p3e1xtj80vi">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seaglass" data-id="element-521" data-mp="10viomu608f7x">
            Your Reader-to-Revenue Score
          </p>
          <p className="mt-4 text-sm font-medium text-text-muted" data-id="element-522" data-mp="1an54x4x794hj">
            {result.totalScore} of 24 points
          </p>
          <h1 className="mx-auto mt-5 max-w-3xl font-serif text-4xl leading-tight text-navy md:text-6xl" data-id="element-523" data-mp="1omwbwlpatkm1">
            {result.band}
          </h1>
          <p className="mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-text-muted" data-id="element-524" data-mp="1jlgqutn4fe3n">
            {content.acknowledgment}
          </p>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-text-muted" data-id="element-525" data-mp="3lfg61q3d6flm">
            {content.meaning}
          </p>
        </header>

        <section className="grid gap-5 py-10 text-left md:grid-cols-2 md:py-12" data-id="element-526" data-mp="1pdmisbdm3938">
          <article className="rounded-3xl border border-gray-200 bg-white p-7 md:p-8" data-id="element-527" data-mp="1ubeihrkhzzhz">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-seaglass" data-id="element-528" data-mp="1z969mjdbjhl4">
              Biggest opportunity
            </p>
            <h2 className="mt-3 font-serif text-3xl text-navy" data-id="element-529" data-mp="23982q44zn2os">
              {DIMENSION_LABEL[result.biggestOpportunity]}
            </h2>
            <p className="mt-4 leading-relaxed text-text-muted" data-id="element-530" data-mp="2d8yvo1dwh9mr">
              {content.opportunity}
            </p>
          </article>

          <article className="rounded-3xl border border-gray-200 bg-white p-7 md:p-8" data-id="element-531" data-mp="2i18ts4p65g04">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-seaglass" data-id="element-532" data-mp="2mz0mh0xn165d">
              Strongest layer
            </p>
            <h2 className="mt-3 font-serif text-3xl text-navy" data-id="element-533" data-mp="2w086qf3bk6gg">
              {DIMENSION_LABEL[result.strongestLayer]}
            </h2>
            <p className="mt-4 leading-relaxed text-text-muted" data-id="element-534" data-mp="kd4xtv0nsq1u">
              {content.nextStep}
            </p>
          </article>
        </section>

        <section className="border-t border-gray-200 py-10 text-left md:py-12" aria-labelledby="dimensions-heading" data-id="element-535" data-mp="fg4uhrv721ef">
          <p className="mb-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-seaglass" data-id="element-536" data-mp="6d3ri01mkqez">
            Your four layers
          </p>
          <h2 id="dimensions-heading" className="sr-only" data-id="element-537" data-mp="132uu6mk2ljvv">
            Your four score dimensions
          </h2>
          <div className="grid gap-3 sm:grid-cols-2" data-id="element-538" data-mp="z2knt8sd8rgh">
            {DIMENSION_ORDER.map(dimension => <article key={dimension} className="rounded-2xl border border-gray-200 bg-white px-5 py-4" data-id="element-539" data-mp="u9hun1ri3k0t">
                <div className="flex items-baseline justify-between gap-4" data-id="element-540" data-mp="pbpqn9dl07g9">
                  <h3 className="font-semibold text-navy" data-id="element-541" data-mp="1ms59hzfwm2tg">
                    {DIMENSION_LABEL[dimension]}
                  </h3>
                  <p className="text-sm font-medium text-text-muted" data-id="element-542" data-mp="1hyk77v7a2mog">
                    {result.dimensions[dimension]} / 6
                  </p>
                </div>
                <p className="mt-1 text-sm text-text-muted" data-id="element-543" data-mp="16l0f7wzr0y1r">
                  {dimensionLevel(result.dimensions[dimension])}
                </p>
              </article>)}
          </div>
        </section>

        <AuditRequestForm score={result} helperText={content.ctaHelper} data-id="element-544" data-mp="xkc1l3e9ozpe" />
      </div>
    </main>;
}
function EmptyScoreState() {
  return <main className="flex min-h-screen w-full items-center justify-center bg-paper px-6 py-12 text-text" data-id="element-545" data-mp="1lb7y2dto4j2z">
      <section className="w-full max-w-xl rounded-3xl border border-gray-200 bg-white p-8 text-center shadow-sm md:p-12" aria-labelledby="empty-score-heading" data-id="element-546" data-mp="1pcjfhw2e1y03">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seaglass" data-id="element-547" data-mp="1gh3pxr8o250b">
          Reader-to-Revenue Score
        </p>
        <h1 id="empty-score-heading" className="mt-4 font-serif text-4xl leading-tight text-navy" data-id="element-548" data-mp="2464sixutilxo">
          This score link is incomplete or invalid.
        </h1>
        <p className="mt-5 leading-relaxed text-text-muted" data-id="element-549" data-mp="2ic6pxpvs15i0">
          Complete the eight-question check to generate a fresh score page.
        </p>
        <a href="/" className="mt-8 inline-flex min-h-[48px] items-center justify-center rounded-xl bg-[#d4a574] px-6 py-3 font-bold text-white transition-colors hover:bg-[#c89860] focus:outline-none focus:ring-2 focus:ring-seaglass" data-id="element-550" data-mp="2s3b4iohjiocb">
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" data-id="element-551" data-mp="1zfoscru8ybdu" />
          Return to Author Leverage
        </a>
      </section>
    </main>;
}